// services/apiService.ts
import type School from '@/models/school';
import type Course from '@/models/course';
import httpClient from '@/common/httpClient';

/**
 * Strip UTF-8 BOM if present.
 */
function stripBom(s: string): string {
  return s.replace(/^\uFEFF/, '');
}

/**
 * Minimal CSV parser with support for:
 * - quoted fields (")
 * - commas inside quoted fields
 * - escaped quotes ("")
 * - CRLF/LF newlines
 * - ignoring blank lines
 *
 * Returns: Array<Record<header, value>>
 */
function parseCsv(text: string): Array<Record<string, string>> {
  text = stripBom(text);

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = '';
  };

  const rowIsAllEmpty = (r: string[]) => r.every((cell) => (cell ?? '').trim() === '');

  const pushRow = () => {
    if (row.length === 0 || rowIsAllEmpty(row)) {
      row = [];
      return;
    }
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        const next = text[i + 1];
        if (next === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }

    if (ch === ',') {
      pushField();
      continue;
    }

    if (ch === '\n') {
      pushField();
      pushRow();
      continue;
    }

    if (ch === '\r') continue;

    field += ch;
  }

  // flush last row
  pushField();
  pushRow();

  if (rows.length === 0) return [];

  const headers = rows[0].map((h) => stripBom((h ?? '').trim()));
  const dataRows = rows.slice(1);

  return dataRows.map((r) => {
    const obj: Record<string, string> = {};
    for (let c = 0; c < headers.length; c++) {
      const key = headers[c] || `__col_${c}`; // handles trailing comma header like OTHER,
      obj[key] = (r[c] ?? '').trim();
    }
    return obj;
  });
}

/**
 * Case-insensitive + BOM-safe field getter.
 */
function getField(r: Record<string, string>, key: string): string {
  if (r[key] != null) return r[key];

  const target = key.toLowerCase();
  for (const k of Object.keys(r)) {
    const normalized = k.replace(/^\uFEFF/, '').trim().toLowerCase();
    if (normalized === target) return r[k] ?? '';
  }
  return '';
}

/**
 * Fetch courses for a specific school from local public CSV:
 * /public/data/<school.slug>/CourseCatalog.csv -> /data/<school.slug>/CourseCatalog.csv
 */
const courseCatalogCache = new Map<string, Course[]>();

export async function fetchSchoolCourses(school: School): Promise<Course[]> {
  const slug = (school as any)?.slug;
  if (!slug) throw new Error(`Missing school.slug for ${school?.name ?? 'unknown school'}`);

  const url = `/data/${slug}/CourseCatalog.csv`;

  // cache hit
  const cached = courseCatalogCache.get(url);
  if (cached) return cached;

  const resp = await fetch(url, { cache: 'no-store' });
  if (!resp.ok) {
    throw new Error(`Failed to fetch course catalog CSV (${resp.status}): ${url}`);
  }

  const contentType = resp.headers.get('content-type') || '';
  const text = await resp.text();

  // If the server returned index.html (router fallback), fail loudly
  const looksHtml =
    contentType.includes('text/html') ||
    text.trim().startsWith('<!DOCTYPE html') ||
    text.trim().startsWith('<html');

  if (looksHtml) {
    throw new Error(`Expected CSV but got HTML from ${url}. Check public/data/${slug}/CourseCatalog.csv path.`);
  }

  const rows = parseCsv(text);

  const courses: Course[] = rows
    .map((r) => {
      const prefix = getField(r, 'PREFIX').trim();
      const num = getField(r, 'NUM').trim();
      const title = getField(r, 'TITLE').trim();
      const courseIdRaw = getField(r, 'COURSE_ID').trim();

      const minCreditsRaw = getField(r, 'CREDITS_MINIMUM').trim();
      const maxUnitsRaw = getField(r, 'MAX_UNITS').trim();

      const minimum_credits = minCreditsRaw ? Number(minCreditsRaw) : 0;
      const maximum_credits = maxUnitsRaw ? Number(maxUnitsRaw) : minimum_credits;

      const code = prefix && num ? `${prefix} ${num}` : `${prefix}${num}`.trim();
      const fullName = code && title ? `${code} - ${title}` : (title || code);

      return {
        // required by Course type
        minimum_credits,
        maximum_credits,

        // fields your UI uses
        id: courseIdRaw ? Number(courseIdRaw) : undefined,
        code,
        full_number: code,
        name: title,
        title,
        full_name: fullName,
      } as Course;
    })
    // Remove blank artifacts (e.g., trailing newline)
    .filter((c) => (c.full_name ?? '').trim() !== '');

  // Nice ordering in SearchSelect
  courses.sort((a: any, b: any) => (a.full_name || '').localeCompare(b.full_name || ''));

  courseCatalogCache.set(url, courses);
  return courses;
}

/**
 * Generate transfer completion plan
 */
export async function generateCompletionPlan(data: {
  source_school: School;
  dest_program: any;
  taken_courses: Course[];
}): Promise<any> {
  const url =
  "https://api.azdegreeplans.com/completion_plan"
  //"http://localhost:8080/completion_plan"

  const { data: resp } = await httpClient.post(url, data, {
    headers: { 'x-api-key': import.meta.env.VITE_CURRICULAR_ANALYTICS_API_KEY },
  });
  return resp;
}

/**
 * Generate two-to-four year transfer plan
 */
export async function generateTwoToFourPlan(data: {
  source_program: any;
  dest_program: any;
}): Promise<any> {

const url =
// "https://8exykg10n2.execute-api.us-west-2.amazonaws.com/transfer-plan-new"
"https://api.azdegreeplans.com/two_to_four_plan"
//"http://localhost:8080/two_to_four_plan"

  const { data: resp } = await httpClient.post(url, data, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': import.meta.env.VITE_CURRICULAR_ANALYTICS_API_KEY,
    },
  });
  return resp;
}

/**
 * Get all available schools
 */
export function getSchools(): {
  startingSchools: School[];
  destinationSchools: School[];
} {
  const startingSchools = [
    { id: 3056, name: 'Pima Community College', slug: 'pima-community-college', disable: false },
    { id: 4657, name: 'Maricopa Community College', slug: 'maricopa-community-college', disable: true },
    { id: 4658, name: 'Eastern Arizona College', slug: 'eastern-arizona-college', disable: true },
    { id: 1, name: 'Tohono O’odham Community College', slug: 'tohono-o’odham-community-college', disable: true },
  ];

  const destinationSchools = [
    { id: 4036, name: 'University of Arizona', slug: 'university-of-arizona', disable: false },
  ];

  return { startingSchools, destinationSchools };
}